import { User, Users, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { UserPersona } from '@/types/persona';
import { usePersona } from '@/contexts/PersonaContext';

const personaConfig = {
  personal: {
    label: 'Personal View',
    description: 'Your timesheet management',
    color: 'bg-green-500 hover:bg-green-600',
    icon: User,
  },
  manager: {
    label: 'Manager View',
    description: 'Team oversight and approvals',
    color: 'bg-blue-500 hover:bg-blue-600',
    icon: Users,
  },
};

interface PersonaBadgeToggleProps {
  className?: string;
}

export function PersonaBadgeToggle({ className }: PersonaBadgeToggleProps) {
  const { currentPersona, setCurrentPersona, availablePersonas } = usePersona();
  const current = personaConfig[currentPersona];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={cn('h-9 px-3 py-2 gap-2', className)}
          aria-label={`Current view: ${current.label}. Click to switch persona.`}
        >
          <span className="font-medium">{current.label}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {availablePersonas.map((persona) => {
          const config = personaConfig[persona];
          const Icon = config.icon;

          return (
            <DropdownMenuItem
              key={persona}
              onClick={() => setCurrentPersona(persona)}
              className="px-3 py-2"
            >
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4" />
                <span className="font-medium">{config.label}</span>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Alternative: Simple Badge variant without dropdown (for reference)
export function PersonaBadgeSimple({ className }: { className?: string }) {
  const { currentPersona } = usePersona();
  const current = personaConfig[currentPersona];
  const CurrentIcon = current.icon;

  return (
    <Badge
      variant="secondary"
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5',
        current.color,
        'text-white',
        className
      )}
    >
      <CurrentIcon className="h-3.5 w-3.5" />
      <span>{current.label}</span>
    </Badge>
  );
}
